/* global Set */
import moment from 'moment';
import {
  audienceTypes,
  notificationLastVisitOptions,
  notificationOrderStatusOptions,
} from 'utils/constants';
import {
  InvestingExperienceEnum,
  investmentPreferenceEnum,
  investmentHorizonEnum,
  investmentAffiliationEnum,
  visaTypeEnum,
  citizenStatusEnum,
  annualNetIncomeEnum,
  taxFilingStatusEnum,
  employmentStatusEnum,
  estimatedNetWorthEnum,
  investmentGoalsEnum,
  investmentStrategiesEnum,
  kycStatusEnums,
} from 'utils/mock';
import {
  WALLET_AMOUNT_OPERATORS,
  WALLET_CURRENCIES,
  WALLET_MOVEMENTS,
  WALLET_OPERATIONS,
  WALLET_PAYMENT_METHODS,
  WALLET_PAYMENT_PROVIDERS,
  WALLET_STATUSES,
} from './walletAudience';
import {
  buildHoldingsAudience,
  showHoldingsOperator,
  supportsHoldings,
} from './holdingsAudience';

/**
 * The audience form in plain words, as sections of `{label, text}` rows.
 *
 * How the backend combines them, and what the review says:
 * - every section, and every field inside a section, must match (AND);
 * - a field with several values matches any one of them (OR), so the text
 *   joins them with "or".
 *
 * Built from form values, so the composer card, the collapsed section
 * headers and the review modal can never disagree.
 */

const list = value =>
  [...new Set([].concat(value ?? []))].filter(
    item => item !== null && item !== undefined && item !== '',
  );

/** "Approved or Pending", from values and their option labels. */
const anyOf = (
  values,
  options = [],
  {valueKey = 'value', labelKey = 'label'} = {},
) =>
  list(values)
    .map(
      value =>
        options.find(option => option[valueKey] === value)?.[labelKey] ?? value,
    )
    .join(' or ');

const enumAnyOf = (values, options) =>
  anyOf(values, options, {valueKey: 'key', labelKey: 'name'});

/** `name;id` select keys keep the name first. */
const namesOf = values =>
  list(values).map(value => String(value).split(';')[0]);

/** "2 Nov 2026 at 17:25"; a midnight time is left out as noise. */
const day = value => {
  const date = moment(value);
  return date.format('HH:mm') === '00:00'
    ? date.format('D MMM YYYY')
    : date.format('D MMM YYYY [at] HH:mm');
};

const between = (from, to) => {
  if (from && to) return `Between ${day(from)} and ${day(to)}`;
  if (from) return `From ${day(from)}`;
  if (to) return `Up to ${day(to)}`;
  return null;
};

const isSet = value => value && value !== 'EITHER';

const yesNo = (value, yes, no) =>
  value === 'YES' ? yes : value === 'NO' ? no : null;

const row = (label, text) => (text ? {label, text} : null);

const titleCase = key =>
  ({IPOS: 'IPOs'}[key] ??
  String(key).charAt(0) +
    String(key).slice(1).toLowerCase().replace(/_/g, ' '));

const PROFILE_STEPS = {
  EmailVerification: 'Email not verified yet',
  PhoneVerification: 'Phone not verified yet',
  Basics: 'Stopped at step one (basics)',
  Preferences: 'Stopped at step two (preferences)',
  Investments: 'Stopped at step three (investments)',
  Customization: 'Stopped at step four (customization)',
};

const ORDER_ASSET_CLASSES = {
  EQUITIES: 'Equities',
  FIXED_INCOMES: 'Fixed income',
  FUNDS: 'Funds',
};

const ORDER_FAMILIES = {
  PUBLIC_EQUITY: 'Stock / public equity',
  IPO: 'IPO',
  PRIVATE_EQUITY: 'Startup / private equity',
  BOND: 'Bond',
  MUTUAL_FUND: 'Mutual fund',
  VENTURE_FUND: 'Venture fund',
};

const count = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const baseSection = (values, lookups) => {
  const audience = values?.audience || 'ALL_USERS';
  const label =
    audienceTypes.find(type => type.value === audience)?.label || audience;
  let text = label;
  if (audience === 'USERS') {
    const n = lookups.recipientCount ?? 0;
    text = `Specific users: ${n} ${n === 1 ? 'person' : 'people'} picked`;
  } else if (audience === 'USERS_BY_GROUP') {
    const names = list(values?.groups).map(
      id => lookups.groups?.find(group => group?.id === id)?.name ?? id,
    );
    text = names.length
      ? `Members of ${names.join(' or ')}`
      : 'Users in group(s): none picked yet';
  } else if (
    ['INVESTORS_IN_A_CAMPAIGN', 'INVESTORS_WITH_RESERVATIONS'].includes(
      audience,
    )
  ) {
    const id =
      audience === 'INVESTORS_IN_A_CAMPAIGN'
        ? values?.liveCampaignId
        : values?.testCampaignId;
    const campaign = lookups.campaigns?.find(item => item?.id === id);
    const name = campaign?.fundraisingCompany?.companyName;
    text = `${label}: ${name || 'no campaign picked yet'}`;
  }
  return [row('Who', text)];
};

const statusSection = values => {
  if (values?.audience === 'USERS') return [];
  return [
    row(
      'Accredited',
      yesNo(
        values?.accredited,
        'Accredited investors only',
        'Non-accredited only',
      ),
    ),
    row(
      'Active investor',
      yesNo(
        values?.activeInvestors,
        'Active investors only',
        'Not active investors',
      ),
    ),
    row(
      'Verified investor',
      yesNo(
        values?.verifiedInvestors,
        'Verified investors only',
        'Not verified investors',
      ),
    ),
    row(
      'Daba Pro',
      yesNo(values?.isDabaPro, 'Daba Pro members only', 'Not Daba Pro members'),
    ),
    row(
      'Last app visit',
      isSet(values?.latestVisit) &&
        anyOf(values.latestVisit, notificationLastVisitOptions),
    ),
    row(
      'Profile completion',
      isSet(values?.profileCompletion) &&
        (PROFILE_STEPS[values.profileCompletion] ?? values.profileCompletion),
    ),
    row('Identity check', anyOf(values?.isKycApproved, kycStatusEnums)),
    row(
      'Document check',
      anyOf(values?.isDocumentCheckApproved, kycStatusEnums),
    ),
    row('AML check', anyOf(values?.isAmlApproved, kycStatusEnums)),
  ];
};

const riskSection = (values, lookups) => {
  if (values?.audience === 'USERS') return [];
  return [
    row(
      'Risk profile',
      list(values?.riskProfile)
        .map(
          id =>
            lookups.riskProfiles?.find(profile => profile?.id === id)?.name ??
            id,
        )
        .join(' or '),
    ),
  ];
};

const investmentSection = values => {
  const ids = list(values?.audienceInstrumentId);
  return [
    row('Orders placed', between(values?.orderFrom, values?.orderTo)),
    row(
      'Order status',
      anyOf(values?.orderStatus, notificationOrderStatusOptions, {
        valueKey: 'key',
        labelKey: 'name',
      }),
    ),
    row(
      'Buy or sell',
      anyOf(values?.orderDirection, [
        {value: 'BUY', label: 'Buy'},
        {value: 'SELL', label: 'Sell'},
      ]),
    ),
    row(
      'Order type',
      anyOf(values?.orderType, [
        {value: 'LIMIT', label: 'Limit'},
        {value: 'MARKET', label: 'Market'},
      ]),
    ),
    row(
      'On hold',
      yesNo(values?.isOrderOnHold, 'Orders on hold', 'Orders not on hold'),
    ),
    row(
      'Asset class',
      list(values?.audienceAssetClassKey)
        .map(key => ORDER_ASSET_CLASSES[key] ?? key)
        .join(' or '),
    ),
    row(
      'Instrument family',
      list(values?.audienceInstrumentKey)
        .map(key => ORDER_FAMILIES[key] ?? key)
        .join(' or '),
    ),
    row(
      'Exact instrument',
      ids.length ? `${count(ids.length, 'specific instrument')}` : null,
    ),
  ];
};

const accountSection = values => {
  const amount = String(values?.walletAmountValue ?? '').trim();
  const [from, to] = values?.walletDates || [];
  return [
    row('Currency', anyOf(values?.walletCurrency, WALLET_CURRENCIES)),
    row('Operation', anyOf(values?.walletOperation, WALLET_OPERATIONS)),
    row('Status', anyOf(values?.walletStatus, WALLET_STATUSES)),
    row(
      'Payment method',
      anyOf(values?.walletPaymentMethod, WALLET_PAYMENT_METHODS),
    ),
    row(
      'Payment provider',
      anyOf(values?.walletPaymentProvider, WALLET_PAYMENT_PROVIDERS),
    ),
    row('Money moved', anyOf(values?.walletMovement, WALLET_MOVEMENTS)),
    row(
      'Amount',
      values?.walletAmountOperator && amount
        ? `${anyOf(
            values.walletAmountOperator,
            WALLET_AMOUNT_OPERATORS,
          )} ${amount}`
        : null,
    ),
    row('Transaction date', between(from, to)),
  ];
};

const holdingsSection = values => {
  const holdings = buildHoldingsAudience(values).portfolioHoldings;
  if (!holdings) return [];
  const min = holdings.minHoldingValue;
  return [
    row(
      'Asset classes',
      list(holdings.assetClasses).map(titleCase).join(' or '),
    ),
    row(
      'Instruments',
      list(values?.holdingsItems)
        .map(item => item?.label ?? item?.value ?? item)
        .join(' or '),
    ),
    row('Exchange', list(holdings.exchanges).join(' or ')),
    row(
      'Match',
      showHoldingsOperator(values) &&
        (holdings.operator === 'AND'
          ? 'Holds every one of the above'
          : 'Holds at least one of the above'),
    ),
    row(
      'Minimum value',
      min &&
        `Each holding worth at least ${min.amount.toLocaleString()} ${
          min.currency
        }`,
    ),
    row(
      'Positions',
      holdings.includeUnsettled
        ? 'Settled and unsettled positions'
        : 'Settled positions only',
    ),
  ];
};

const locationSection = values => [
  row('Country of residence', list(values?.countryOfResidence).join(' or ')),
  row('State', list(values?.state).join(' or ')),
  row('City', list(values?.city).join(' or ')),
  row('Country of birth', namesOf(values?.countryOfBirth).join(' or ')),
  row('Citizenship', namesOf(values?.countryOfCitizenship).join(' or ')),
  row('U.S. visa type', enumAnyOf(values?.visaType, visaTypeEnum)),
  row(
    'Citizenship status',
    enumAnyOf(values?.citizenStatus, citizenStatusEnum),
  ),
];

const financialSection = values => [
  row('Annual net income', enumAnyOf(values?.netIncome, annualNetIncomeEnum)),
  row(
    'Tax filing status',
    enumAnyOf(values?.taxFilingStatus, taxFilingStatusEnum),
  ),
  row(
    'Employment status',
    enumAnyOf(values?.employmentStatus, employmentStatusEnum),
  ),
  row(
    'Estimated net worth',
    enumAnyOf(values?.estimatedNetWorth, estimatedNetWorthEnum),
  ),
];

const investorSection = values => [
  row(
    'Experience',
    enumAnyOf(values?.investmentExperience, InvestingExperienceEnum),
  ),
  row(
    'Preference',
    enumAnyOf(values?.investmentPreference, investmentPreferenceEnum),
  ),
  row('Horizon', enumAnyOf(values?.investmentHorizon, investmentHorizonEnum)),
  row(
    'Affiliation',
    enumAnyOf(values?.investmentAffiliation, investmentAffiliationEnum),
  ),
  row('Goals', enumAnyOf(values?.investmentGoals, investmentGoalsEnum)),
  row(
    'Strategies',
    enumAnyOf(values?.investmentStrategies, investmentStrategiesEnum),
  ),
];

const UNITS = {DAY: 'day', WEEK: 'week', MONTH: 'month', YEAR: 'year'};

const signupSection = values => {
  if (values?.signupDate === 'RANGE') {
    return [row('Signed up', between(values?.from, values?.to))];
  }
  const n = Number(values?.durationValue);
  if (values?.signupDate === 'DURATION' && values?.durationUnit && n > 0) {
    return [
      row('Signed up', `In the last ${count(n, UNITS[values.durationUnit])}`),
    ];
  }
  return [];
};

/**
 * Section order matches the form. `rule` says how the rows inside combine;
 * `hint` is what the section is for, shown while nothing is set.
 */
export const AUDIENCE_SECTIONS = [
  {
    key: 'base',
    title: 'Audience',
    hint: 'Who the notification starts from.',
    build: baseSection,
  },
  {
    key: 'status',
    title: 'Account Status & Verification',
    hint: 'Accredited, active, verified, Daba Pro, app visits and KYC checks.',
    rule: 'Users must match every field set here.',
    explain:
      "Looks at each user's account: whether they are accredited, active or verified, their Daba Pro membership, app visits and identity checks.",
    groups: [
      [
        'Investor status',
        [
          'Accredited',
          'Active investor',
          'Verified investor',
          'Daba Pro',
          'Risk profile',
        ],
      ],
      ['Engagement', ['Last app visit', 'Profile completion']],
      ['Checks', ['Identity check', 'Document check', 'AML check']],
    ],
    build: (values, lookups) => [
      ...statusSection(values),
      ...riskSection(values, lookups),
    ],
  },
  {
    key: 'investment',
    title: 'Investment Activity',
    hint: 'Users by the orders they placed: dates, status, direction, instruments.',
    rule: 'Users need orders matching every field set here.',
    explain: 'Looks at the orders users placed.',
    groups: [
      ['When', ['Orders placed']],
      ['The order', ['Order status', 'Buy or sell', 'Order type', 'On hold']],
      [
        'What was traded',
        ['Asset class', 'Instrument family', 'Exact instrument'],
      ],
    ],
    build: investmentSection,
  },
  {
    key: 'account',
    title: 'Account Activity',
    hint: 'Users by their deposits and withdrawals.',
    rule: 'One single transaction must match every field set here.',
    explain:
      'Looks at deposits and withdrawals. One single transaction must meet every condition; two different transactions that each meet some of them do not count.',
    groups: [
      ['The transaction', ['Currency', 'Operation', 'Status', 'Amount']],
      [
        'How the money moved',
        ['Payment method', 'Payment provider', 'Money moved'],
      ],
      ['When', ['Transaction date']],
    ],
    build: accountSection,
  },
  {
    key: 'holdings',
    title: 'Portfolio Holdings',
    hint: 'Users by what they hold today: classes, instruments, exchanges.',
    rule: 'Checked against holdings when the notification is sent.',
    explain:
      'Looks at what users hold in their portfolio when the notification is sent, not when it is scheduled.',
    groups: [
      ['What they hold', ['Asset classes', 'Instruments', 'Exchange']],
      ['Rules', ['Match', 'Minimum value', 'Positions']],
    ],
    build: holdingsSection,
  },
  {
    key: 'location',
    title: 'Location & Citizenship',
    hint: 'Where users live and their citizenship.',
    rule: 'Users must match every field set here.',
    explain: 'Looks at where users live and the countries they belong to.',
    groups: [
      ['Where they live', ['Country of residence', 'State', 'City']],
      [
        'Citizenship',
        [
          'Country of birth',
          'Citizenship',
          'U.S. visa type',
          'Citizenship status',
        ],
      ],
    ],
    build: locationSection,
  },
  {
    key: 'financial',
    title: 'Financial Profile',
    hint: 'Income, tax filing, employment and net worth.',
    rule: 'Users must match every field set here.',
    explain: 'Looks at the financial details users gave when they signed up.',
    build: financialSection,
  },
  {
    key: 'investor',
    title: 'Investor Profile',
    hint: 'Experience, preferences, horizon, goals and strategies.',
    rule: 'Users must match every field set here.',
    explain:
      'Looks at the investing profile users filled in: experience, preferences and goals.',
    build: investorSection,
  },
  {
    key: 'signup',
    title: 'Signup Time',
    hint: 'When users created their account.',
    explain: 'Looks at when users created their Daba account.',
    build: signupSection,
  },
];

/** Every section with its set rows; the base section is always there. */
export const describeAudience = (values, lookups = {}) =>
  AUDIENCE_SECTIONS.filter(
    section => section.key !== 'holdings' || supportsHoldings(values?.audience),
  ).map(({build, ...section}) => ({
    ...section,
    rows: build(values ?? {}, lookups).filter(Boolean),
  }));

/** One section's rows, for its collapsed header. */
export const describeSection = (key, values, lookups) =>
  describeAudience(values, lookups).find(section => section.key === key)
    ?.rows ?? [];

/**
 * One short line for a collapsed header: the names of the first few fields
 * that are set, "+N more" for the rest. Values live in the full explanation.
 */
export const headerLine = (rows, shown = 3) => {
  const names = rows.slice(0, shown).map(item => item.label);
  const more = rows.length - names.length;
  return more > 0 ? `${names.join(', ')} +${more} more` : names.join(', ');
};

/**
 * A section's rows sorted into its themed groups, for the explanation modal.
 * Sections without groups, and rows no group names, land in one untitled
 * group so nothing set is ever left out.
 */
export const groupRows = section => {
  const used = new Set();
  const grouped = (section.groups ?? [])
    .map(([title, labels]) => {
      const rows = section.rows.filter(item => labels.includes(item.label));
      rows.forEach(item => used.add(item.label));
      return {title, rows};
    })
    .filter(group => group.rows.length);
  const rest = section.rows.filter(item => !used.has(item.label));
  return rest.length ? [...grouped, {title: null, rows: rest}] : grouped;
};

/** "Error or Cancelled" -> ["Error", "Cancelled"], shown as chips. */
export const splitValues = text => String(text).split(' or ');
